// Production → Audits → BOM lane. Recipe-abstract walk with recursive
// sub-audits. Distinct from desk needs_review / pending lines.
import { useEffect, useState } from "react";
import { SectionCard } from "@/components/common/SectionCard";
import {
  cardMutedTextClass,
  cardSurfaceClass,
  cardTitleTextClass,
  ghostBtnClass,
  mfgSecondaryBtnClass,
} from "@/components/manufacturing/manufacturingSurfaceClasses";
import { ManufacturingGateBanner } from "./ManufacturingGateBanner";
import { ManufacturingBomAuditOpenForm } from "./ManufacturingBomAuditOpenForm";
import { ManufacturingBomAuditWalk } from "./ManufacturingBomAuditWalk";
import { useManufacturingBomAudit } from "./useManufacturingBomAudit";
import { useManufacturingEntitlementGate } from "./useManufacturingEntitlementGate";
import { listManufacturingRecipes } from "@/lib/manufacturing/recipesClient";
import { listManufacturingPlants } from "@/lib/manufacturingClient";
import type { MfgBomAuditKind } from "@/lib/manufacturing/types/bomAudit";
import { MFG_BOM_AUDIT_KIND_LABELS } from "@/lib/manufacturing/types/bomAudit";

export function ManufacturingBomAuditPanel(props: {
  /** When true, skip outer page chrome (Audits host owns title/slider). */
  embedded?: boolean;
  onSessionActiveChange?: (active: boolean) => void;
} = {}) {
  const gate = useManufacturingEntitlementGate();
  const audit = useManufacturingBomAudit({ enabled: gate.enabled });

  const [recipes, setRecipes] = useState<Array<{ id: string; name: string; code?: string }>>(
    []
  );
  const [plants, setPlants] = useState<
    Array<{ id: string; name: string; plant_code?: string }>
  >([]);
  const [recipeId, setRecipeId] = useState("");
  const [auditKind, setAuditKind] = useState<MfgBomAuditKind>("build_conformance");
  const [plantId, setPlantId] = useState("");
  const [openNotes, setOpenNotes] = useState("");

  useEffect(() => {
    props.onSessionActiveChange?.(Boolean(audit.session));
  }, [audit.session, props.onSessionActiveChange]);

  useEffect(() => {
    if (!gate.enabled) return;
    void (async () => {
      const [rRes, pRes] = await Promise.all([
        listManufacturingRecipes(),
        listManufacturingPlants(),
      ]);
      if (rRes.ok && rRes.data.ok) {
        setRecipes(
          rRes.data.recipes.map((r) => ({
            id: r.id,
            name: r.name,
            code: r.code,
          }))
        );
      }
      if (pRes.ok && pRes.data.ok) {
        setPlants(
          (Array.isArray(pRes.data.plants) ? pRes.data.plants : []).map((p) => ({
            id: p.id,
            name: p.name,
            plant_code: p.plant_code,
          }))
        );
      }
    })();
  }, [gate.enabled]);

  if (!gate.enabled) {
    return props.embedded ? null : <ManufacturingGateBanner gate={gate} />;
  }

  const body = (
    <>
      {audit.error ? (
        <div className="mb-3 rounded border border-red-200 bg-red-50 px-3 py-2 text-sm text-red-800">
          {audit.error}
          <button
            type="button"
            className={`${ghostBtnClass} ml-2`}
            onClick={() => audit.setError(null)}
          >
            Dismiss
          </button>
        </div>
      ) : null}

      {audit.session ? (
        <ManufacturingBomAuditWalk
          session={audit.session}
          nodes={audit.nodes}
          stackDepth={audit.stack.length}
          sessionNotes={audit.sessionNotes}
          busy={audit.busy}
          onSessionNotes={audit.setSessionNotes}
          onSaveNode={(id, patch) => void audit.saveNode(id, patch)}
          onStartSubAudit={(id) => void audit.startSubAudit(id)}
          onOpenSubAudit={(id) => void audit.loadSession(id, true)}
          onComplete={() => void audit.complete()}
          onLeavePartial={() => void audit.leavePartial()}
          onAbandon={() => void audit.abandon()}
          onGoUp={() => void audit.goUp()}
        />
      ) : (
        <div className="grid gap-4 lg:grid-cols-2">
          <ManufacturingBomAuditOpenForm
            recipes={recipes}
            plants={plants}
            recipeId={recipeId}
            auditKind={auditKind}
            plantId={plantId}
            notes={openNotes}
            busy={audit.busy}
            onRecipeId={setRecipeId}
            onAuditKind={setAuditKind}
            onPlantId={setPlantId}
            onNotes={setOpenNotes}
            onOpen={() =>
              void audit.openAudit({
                recipe_id: recipeId,
                audit_kind: auditKind,
                plant_id: plantId || null,
                notes: openNotes,
              })
            }
          />
          <div className={`p-4 ${cardSurfaceClass}`}>
            <div className={`mb-2 text-sm font-medium ${cardTitleTextClass}`}>
              Open / partial / abandoned
            </div>
            <ul className="flex max-h-80 flex-col gap-2 overflow-auto">
              {audit.sessions.map((s) => {
                const recipe = recipes.find((r) => r.id === s.recipe_id);
                return (
                  <li
                    key={s.id}
                    className="flex items-center justify-between gap-2 rounded border border-gray-100 px-2 py-2 text-sm dark:border-zinc-800"
                  >
                    <div className="min-w-0">
                      <div className={`truncate ${cardTitleTextClass}`}>
                        {recipe
                          ? recipe.code
                            ? `${recipe.code} — ${recipe.name}`
                            : recipe.name
                          : s.recipe_id.slice(0, 8)}
                      </div>
                      <div className={`text-xs ${cardMutedTextClass}`}>
                        {MFG_BOM_AUDIT_KIND_LABELS[s.audit_kind]} · {s.status}
                      </div>
                    </div>
                    <button
                      type="button"
                      className={mfgSecondaryBtnClass}
                      disabled={audit.busy}
                      onClick={() => {
                        if (s.status === "open") void audit.loadSession(s.id, false);
                        else void audit.reopen(s.id);
                      }}
                    >
                      {s.status === "open" ? "Resume" : "Reopen"}
                    </button>
                  </li>
                );
              })}
              {audit.sessions.length === 0 ? (
                <li className={`text-xs ${cardMutedTextClass}`}>No resumable audits.</li>
              ) : null}
            </ul>
          </div>
        </div>
      )}
    </>
  );

  if (props.embedded) {
    return <div className="flex flex-col gap-3">{body}</div>;
  }

  return (
    <div className="flex flex-col gap-4 p-4">
      <SectionCard
        title="BOM Audit"
        description="Walk a recipe parts list or check how an active build uses parts. Drill into sub-assemblies, uses, and named variants. Incomplete walks leave a partial session you can reopen."
      >
        {body}
      </SectionCard>
    </div>
  );
}
